import { useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { auth } from '@/api/endpoints'
import type { User } from '@/api/types'
import { cpoScope, session } from './session'

interface AuthState {
  user: User | null
  login: (identifier: string, password: string) => Promise<User>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  /** Superadmin: which operator's data the CPO screens show. */
  scope: number | null
  setScope: (cpoId: number | null) => void
}

const Ctx = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const [user, setUser] = useState<User | null>(() => session.get()?.user ?? null)
  const [scope, setScopeState] = useState<number | null>(() => cpoScope.get())

  useEffect(() => {
    let previous = session.get()?.user
    return session.subscribe((s) => {
      const next = s?.user
      if (previous?.id !== next?.id || previous?.role !== next?.role || previous?.viewer_station_id !== next?.viewer_station_id) {
        void qc.cancelQueries()
        qc.clear()
      }
      previous = next
      setUser(next ?? null)
    })
  }, [qc])
  useEffect(() => cpoScope.subscribe((next) => {
    void qc.cancelQueries({ predicate: (q) => q.queryKey[0] !== 'admin' })
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'admin' })
    setScopeState(next)
  }), [qc])

  const login = useCallback(async (identifier: string, password: string) => {
    const res = await auth.login(identifier.trim(), password)
    session.set({ accessToken: res.access_token, refreshToken: res.refresh_token, user: res.user })
    return res.user
  }, [])

  const logout = useCallback(async () => {
    const pending = auth.logout()
    session.set(null)
    try {
      await pending
    } catch {
      /* the token may already be gone */
    }
  }, [])

  const refreshUser = useCallback(async () => {
    const original = session.get()
    const me = await auth.me()
    const s = session.get()
    if (s && s.user.id === original?.user.id && s.refreshToken === original?.refreshToken) session.set({ ...s, user: me })
  }, [])

  const setScope = useCallback(
    (cpoId: number | null) => {
      cpoScope.set(cpoId)
    },
    [],
  )

  // Refresh the profile once per load (role or station may have changed).
  useEffect(() => {
    if (session.get()) refreshUser().catch(() => undefined)
  }, [refreshUser])

  const value = useMemo(() => ({ user, login, logout, refreshUser, scope, setScope }), [user, login, logout, refreshUser, scope, setScope])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth outside AuthProvider')
  return ctx
}
